/**
 * Global theme — a military / old-CRT / radar aesthetic layered on top of the
 * Staffa component library.
 *
 * Staffa derives every surface and token from a small palette of `--s-*` CSS
 * variables (see staffa/src/theme.ts). We re-skin it by overriding those
 * variables through `A.cssVars` (which Aberdeen emits *after* Staffa's own
 * `:root` rules, so our values win), force dark mode (the CRT look is always
 * dark), and add a few global flourishes: a phosphor monospace font, a
 * full-screen scanline overlay, and a CRT "screen" treatment for the
 * `.s-s.primary` surface.
 */
import A from "aberdeen";
import * as S from "staffa";
import { radar } from "staffa/icons.js";
import type { IconOptions } from "staffa/icons.js";

/** The WildHunt phosphor palette. Hunter red / runner blue match the server. */
export const COLORS = {
	green: "#00FF41",
	blue: "#00BFFF",
	red: "#FF3322",
	amber: "#FFB300",
	screen: "#050F05",
} as const;

/** Faint horizontal scanlines — the signature CRT texture. */
const SCANLINES =
	"repeating-linear-gradient(0deg, transparent 0, transparent 2px, rgba(0,0,0,0.22) 3px)";
/** A soft green phosphor wash, brightest along the top edge. */
const SCREEN_GLOW =
	"radial-gradient(120% 100% at 50% 0%, rgba(0,255,65,0.10), transparent 60%)";

export function installTheme() {
	A.setSpacingCssVars();

	// The CRT world is inherently dark; pin it regardless of OS preference.
	S.setDarkMode(true);

	// ── Palette ──────────────────────────────────────────────────────────────
	// The settable brand/semantic colours; Staffa 0.7 derives every contextual
	// token (`--s-text`/`--s-muted`/`--s-faint`/…) from these and the surfaces.
	Object.assign(A.cssVars, {
		"s-primary": COLORS.green,
		"s-danger": COLORS.red,
		"s-success": COLORS.green,
		"s-warning": COLORS.amber,
		"s-focus": "rgba(0,255,65,0.40)",
		"s-radius-sm": "2px",
		"s-radius": "2px",
		"s-radius-lg": "3px",
	});

	// ── Neutral surfaces (page / panel / raised) ─────────────────────────────
	// Staffa 0.7 paints neutral surfaces by writing `--s-bg`/`--s-text` straight
	// onto `:root` (the page) and each nested `.neutral`. We re-skin those with
	// the near-black CRT greens and phosphor-green ink; `--s-muted`/`--s-faint`
	// then derive from that text/bg pair automatically. (Inserted after Staffa's
	// own mode block, so our values win at equal specificity.)
	A.insertGlobalCss({
		":root, .s-s.neutral": `--s-bg:${COLORS.screen} --s-text:$s-primary`,
		".s-s.neutral": "--s-bg:#081608 --s-text:$s-primary",
		".s-s.neutral .s-s.neutral": "--s-bg:#0C200C --s-text:$s-primary",
	});

	// ── The CRT "screen" surface ─────────────────────────────────────────────
	// Override `.s-s.primary` so a primary surface reads as a lit radar screen:
	// a near-black-green panel, phosphor-green ink, scanlines, an inner glow and
	// a hairline green border. Used for the app's signature framed panels.
	A.insertGlobalCss({
		".s-s.primary": "--s-bg:#040C04 --s-text:$s-primary",
		".s-s.primary:not(.tonal):not(.outlined)":
			`background: ${SCANLINES}, ${SCREEN_GLOW}, #040C04;` +
			"box-shadow: inset 0 0 36px rgba(0,255,65,0.06);" +
			"border: 1px solid #1A3A1A;",
	});

	// ── Primary surfaces: dark ink ───────────────────────────────────────────
	// A role-less `.s-s` is Staffa's bright-green primary fill (default buttons —
	// CREATE GAME / START GAME / JOIN — and any other primary surface). That vivid
	// green reads best with dark ink, so override the near-white default text to a
	// near-black green (not pure black — a CRT never quite reaches it), for every
	// primary surface and not just buttons. The `:not()` guards keep this off the
	// surfaces that must stay phosphor-green: neutral panels/page and the literal
	// `.s-s.primary` "screen" override above. `.danger`/`.success`/`.warning`/
	// `.link` (and tonal/outlined variants) keep their own ink.
	A.insertGlobalCss({
		".s-s:not(.neutral):not(.primary):not(.danger):not(.success):not(.warning):not(.link):not(.tonal):not(.outlined)":
			"--s-text:#021002 --s-accent:#021002",
	});

	// ── Global CRT flourishes ────────────────────────────────────────────────
	A.insertGlobalCss({
		// Fixed-width terminal type everywhere.
		"*": "font-family: 'Courier New', Courier, monospace !important;",
		body: "background:$s-bg user-select:none -webkit-user-select:none",
		// Headings glow like phosphor and read as stencilled labels.
		"h1, h2, h3": "text-transform:uppercase letter-spacing:0.12em",
		h1: "text-shadow: 0 0 8px $s-primary, 0 0 18px rgba(0,255,65,0.45);",
		// Range slider (zone radius / duration) tracks the brand green.
		"input[type=range]": "accent-color: $s-primary; width:100%",
		"::selection": "background:$s-primary color:#021002",

		// Full-screen scanline overlay — the ambient CRT, above all content.
		"body::after":
			"content:'' position:fixed inset:0 pointer-events:none z-index:9999" +
			" background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.06) 2px, rgba(0,0,0,0.06) 4px);",

		// The app-shell title: solid phosphor glow instead of Staffa's gradient
		// text clip, so the WILDHUNT wordmark reads as a lit CRT readout.
		".s-main > header .s-title, .s-main > header .s-header-icon":
			"background:none -webkit-background-clip:border-box background-clip:border-box" +
			" -webkit-text-fill-color:$s-primary color:$s-primary" +
			" text-shadow: 0 0 8px rgba(0,255,65,0.55); letter-spacing:0.22em",

		// A slowly rotating "scanning" glyph. A real CSS animation (not a JS
		// re-render) so ShoTest's `animation: none` test rule freezes it for
		// stable screenshots.
		"@keyframes wildhunt-spin": {
			from: "transform: rotate(0deg)",
			to: "transform: rotate(360deg)",
		},
		".scan-spinner":
			"display:inline-block animation: wildhunt-spin 4s linear infinite;",
	});
}

/** Optional adornments for {@link drawSectionHeader}. */
export interface SectionHeaderOptions {
	/** A leading icon drawn before the label (e.g. a staffa/icons function). */
	icon?: (opts?: IconOptions) => void;
	/** Trailing content drawn after the label, before the right-hand rule. */
	trailing?: () => void;
}

/** Renders a ─── LABEL ─── section divider in muted phosphor. */
export function drawSectionHeader(label: string, opts: SectionHeaderOptions = {}) {
	A(
		"div display:flex align-items:center gap:$2 fg:$s-muted font-size:0.72rem letter-spacing:0.14em",
		() => {
			A("div flex:1 h:1px background:$s-faint");
			if (opts.icon) opts.icon({ size: "0.9rem" });
			A(`span #${label}`);
			if (opts.trailing) opts.trailing();
			A("div flex:1 h:1px background:$s-faint");
		},
	);
}

/**
 * Slowly rotating radar sweep that signals the app is scanning/listening.
 *
 * Implemented as a CSS `@keyframes` animation (see `installTheme`) rather than a
 * JS-driven re-render so ShoTest's test-mode `animation: none` rule freezes it
 * for stable screenshots.
 */
export function drawScanSpinner() {
	A("span.scan-spinner display:inline-flex fg:$s-muted", () =>
		radar({ size: "0.95rem" }),
	);
}
